/**
 * Self-service off-boarding (17 Jul) — the app-wide countdown.
 *
 * Mounted ONCE, in App.js's ProtectedRoute, so it rides every authenticated
 * page without touching each page's own layout. It self-gates hard: it renders
 * nothing unless the signed-in user is an ADMIN of a tenant that is
 * mid-closure. For ~every user ~all the time it is a single cheap GET that
 * returns {closing:false} and then null — no DOM, no layout impact.
 *
 * Rendered as a FIXED bar (bottom-centre) on purpose: every page here uses its
 * own full-height (h-screen) layout, so a normal in-flow banner would fight
 * them. A floating bar sidesteps all of that and is always visible.
 *
 * Employees never see this — during the grace they can't log in to a closing
 * tenant at all (the login gate blocks them), and before the date the account
 * is simply open (the banner self-gates to admins). So the audience is exactly
 * the admins who can undo.
 *
 * TWO PHASES since 22/09/2026 (Terms §8 — cancelling runs to the end of the
 * paid period). `/billing/account-status` says which:
 *   'pending' — cancelled, paid up, nothing locked. Amber: "closes on <date>,
 *               until then everything works as normal".
 *   'grace'   — closed (employees out, admins in), 30 days to the purge. Red,
 *               unchanged from 17 Jul.
 * Both phases use the same undo (/billing/reinstate), which knows which it is.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { bottomDock } from '../lib/bottomDock';
import { AlertTriangle, Hourglass, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { billingApi } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

function gb(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// Cross-component sync. The banner (App-level) and the Settings "Close account"
// section are separate components that don't share state, so a close/reinstate
// in one left the other stale until a refresh (Rob, 17 Jul). Anyone who changes
// the closing state fires this; both listen and re-check. Exported so Settings
// uses the same string.
export const ACCOUNT_STATUS_CHANGED = 'hh:account-status-changed';

export default function AccountClosingBanner() {
  const { user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === 'admin';   // NOT directors/managers — see the gate

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      if (!isAuthenticated || !isAdmin) { setStatus(null); return; }
      billingApi.accountStatus()
        .then((r) => { if (alive) setStatus(r.data); })
        .catch(() => { if (alive) setStatus(null); });
    };
    refresh();
    // Re-check when a close/reinstate happens anywhere (e.g. from Settings).
    window.addEventListener(ACCOUNT_STATUS_CHANGED, refresh);
    return () => { alive = false; window.removeEventListener(ACCOUNT_STATUS_CHANGED, refresh); };
  }, [isAuthenticated, isAdmin]);

  if (!isAdmin || !status?.closing) return null;

  const reinstate = async () => {
    setBusy(true);
    try {
      const r = await billingApi.reinstateAccount();
      toast.success(r.data?.message || 'Your account is back.');
      setStatus({ closing: false });
      window.dispatchEvent(new Event(ACCOUNT_STATUS_CHANGED));   // tell Settings
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reinstate the account');
    } finally {
      setBusy(false);
    }
  };

  const pending = status.phase === 'pending';

  // ⚠️ Into the shared bottom dock (lib/bottomDock.js) — see BillingStatus's BottomBar.
  // A slim strip across the top since 29/10/2026 (lib/bottomDock.js) — it pushes the page down.
  return createPortal(
      <div className={`border-b px-4 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 ${
        pending ? 'border-hive-orange/30 bg-hive-pending-bg dark:bg-[#2A2A3A]' : 'border-red-500/30 bg-hive-danger-bg dark:bg-red-950'}`}>
        {pending
          ? <Hourglass className="w-5 h-5 text-amber-500 flex-shrink-0" />
          : <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />}
        <div className="text-[13px] text-center">
          {pending ? (
            <>
              <span className="font-medium">Your account closes on {gb(status.close_on)}</span>
              <span className="hidden sm:inline text-muted-foreground">
                {' '}— the end of the period you've paid for. Until then everything works as normal.
              </span>
            </>
          ) : (
            <>
              <span className="font-medium">Your account closes on {gb(status.purge_on)}</span>
              {typeof status.days_left === 'number' && (
                <span className="hidden sm:inline text-muted-foreground"> — {status.days_left} day(s) left. </span>
              )}
              <span className="hidden sm:inline text-muted-foreground">Nothing is deleted yet.</span>
            </>
          )}
        </div>
        <Button size="sm" className="btn-primary flex-shrink-0 h-7 px-3 text-xs" onClick={reinstate} disabled={busy}>
          {busy ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" />…</>) : 'Keep our account'}
        </Button>
      </div>,
    bottomDock(),
  );
}
